(() => {
  const scrollKey = 'flexora-locale-scroll';
  const submitSessionKey = 'flexora-application-submit-session';
  let cleanupTasks = [];

  function storageGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (_error) { return null; }
  }

  function storageSet(key, entry) {
    try { window.sessionStorage.setItem(key, entry); } catch (_error) { /* Storage is optional. */ }
  }

  function storageRemove(key) {
    try { window.sessionStorage.removeItem(key); } catch (_error) { /* Storage is optional. */ }
  }

  function listen(target, type, handler, options) {
    target.addEventListener(type, handler, options);
    cleanupTasks.push(() => target.removeEventListener(type, handler, options));
  }

  function cleanup() {
    document.documentElement.classList.remove('reveal-ready');
    cleanupTasks.splice(0).forEach((task) => task());
  }

  function init() {
    cleanup();

    const savedScroll = storageGet(scrollKey);
    if (savedScroll !== null) {
      requestAnimationFrame(() => {
        window.scrollTo({ top: Number(savedScroll), behavior: 'instant' });
        storageRemove(scrollKey);
      });
    }

    document.querySelectorAll('[data-language-option]').forEach((option) => {
      const handleLanguageChange = () => {
        if (!option.checked) return;
        storageSet(scrollKey, String(window.scrollY));
        option.closest('form')?.submit();
      };
      listen(option, 'change', handleLanguageChange);
    });

    document.querySelectorAll('[data-application-form]').forEach((form) => {
      try {
      const fields = [...form.querySelectorAll('input:not([type="hidden"]), select, textarea')];
      const submitButton = form.querySelector('[data-submit-button]');
      const submitLabel = form.querySelector('[data-submit-label]');
      const submittingLabel = form.querySelector('[data-submitting-label]');
      const slowMessage = form.querySelector('[data-slow-message]');
      const retryButton = form.querySelector('[data-retry-button]');
      const serverError = form.querySelector('[data-server-error]');
      let isSubmitting = false;
      let slowTimer;

      form.querySelectorAll('[data-utm-field]').forEach((field) => {
        const parameter = field.dataset.utmField;
        const storageKey = `flexora-${parameter}`;
        const queryValue = new URLSearchParams(window.location.search).get(parameter);
        try {
          if (queryValue) storageSet(storageKey, queryValue);
          field.value = queryValue || storageGet(storageKey) || '';
        } catch (_error) {
          field.value = queryValue || '';
        }
      });

      const errorFor = (field) => field.closest('.application-field')?.querySelector('[data-field-error]');
      const validationMessage = (field) => {
        const value = field.value.trim();
        if (!value) return field.dataset.requiredMessage || '';
        if (field.type === 'text' && value.length < 2) return field.dataset.patternMessage || '';
        if (field.type === 'number' && Number(value) < Number(field.min)) return field.dataset.minMessage || '';
        if (field.type === 'email' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return field.dataset.patternMessage || '';
        if (field.type === 'tel' && !/^\+?(?:[() .-]*\d){10,15}[() .-]*$/.test(value)) return field.dataset.patternMessage || '';
        return '';
      };
      const renderFieldError = (field, message) => {
        const error = errorFor(field);
        if (!error) return;
        error.textContent = message;
        error.hidden = !message;
        if (message) field.setAttribute('aria-invalid', 'true');
        else field.removeAttribute('aria-invalid');
      };

      fields.forEach((field) => {
        const clearResolvedError = () => {
          if (!validationMessage(field)) renderFieldError(field, '');
        };
        listen(field, 'input', clearResolvedError);
        listen(field, 'change', clearResolvedError);
      });

      const handleSubmit = (event) => {
        const submittedAt = form.querySelector('[data-submitted-at]');
        if (submittedAt) submittedAt.value = new Date().toISOString();
        if (isSubmitting) {
          event.preventDefault();
          return;
        }
        let firstInvalid;
        fields.forEach((field) => {
          const message = validationMessage(field);
          renderFieldError(field, message);
          if (message && !firstInvalid) firstInvalid = field;
        });
        if (firstInvalid) {
          event.preventDefault();
          firstInvalid.focus();
          return;
        }
        storageSet(submitSessionKey, String(Date.now()));
        isSubmitting = true;
        form.classList.add('is-submitting');
        if (submitButton) submitButton.disabled = true;
        if (submitLabel) submitLabel.hidden = true;
        if (submittingLabel) submittingLabel.hidden = false;
        slowTimer = window.setTimeout(() => {
          if (slowMessage) slowMessage.hidden = false;
        }, 7000);
        cleanupTasks.push(() => window.clearTimeout(slowTimer));
      };
      listen(form, 'submit', handleSubmit);

      if (retryButton) {
        listen(retryButton, 'click', () => {
          if (serverError) serverError.hidden = true;
          fields[0]?.focus();
        });
      }

      if (serverError) storageRemove(submitSessionKey);
      if (serverError && !serverError.dataset.focusHandled) {
        serverError.dataset.focusHandled = 'true';
        serverError.focus();
      }

      const captchaProtect = window.Shopify?.captcha?.protect;
      if (!form.dataset.captchaProtected && typeof captchaProtect === 'function') {
        window.Shopify.captcha.protect(form);
        form.dataset.captchaProtected = 'true';
      }

      form.noValidate = true;
      } catch (_error) {
        form.noValidate = false;
      }
    });

    document.querySelectorAll('[data-copy-whatsapp]').forEach((button) => {
      listen(button, 'click', async () => {
        try {
          await navigator.clipboard.writeText(button.dataset.copyValue);
          button.textContent = button.dataset.copiedLabel;
        } catch (_error) {
          window.prompt('', button.dataset.copyValue);
        }
      });
    });

    const reveals = [...document.querySelectorAll('.reveal')].filter((element) => !element.classList.contains('is-visible'));
    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    reveals.forEach((element, index) => {
      element.style.setProperty('--reveal-delay', `${Math.min(index * 80, 240)}ms`);
    });
    if (reducedMotion || !('IntersectionObserver' in window)) {
      reveals.forEach((element) => element.classList.add('is-visible'));
    } else if (reveals.length) {
      let revealObserver;
      try {
        revealObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          });
        }, { rootMargin: '0px 0px -8%' });
        reveals.forEach((element) => revealObserver.observe(element));
        document.documentElement.classList.add('reveal-ready');
        cleanupTasks.push(() => revealObserver.disconnect());
      } catch (_error) {
        revealObserver?.disconnect();
        document.documentElement.classList.remove('reveal-ready');
        reveals.forEach((element) => element.classList.add('is-visible'));
      }
    }

    const sticky = document.querySelector('[data-mobile-sticky-apply]');
    if (!sticky) return;

    const hero = document.querySelector('.hero');
    const application = document.querySelector('#apply');
    let heroVisible = true;
    let applicationVisible = false;
    let fieldFocused = false;

    const updateSticky = () => {
      sticky.classList.toggle('is-hidden', heroVisible || applicationVisible || fieldFocused);
    };

    if ('IntersectionObserver' in window) {
      if (hero) {
        const heroObserver = new IntersectionObserver(([entry]) => {
          heroVisible = entry.isIntersecting;
          updateSticky();
        });
        heroObserver.observe(hero);
        cleanupTasks.push(() => heroObserver.disconnect());
      }

      if (application) {
        const applicationObserver = new IntersectionObserver(([entry]) => {
          applicationVisible = entry.isIntersecting;
          updateSticky();
        }, { threshold: 0.05 });
        applicationObserver.observe(application);
        cleanupTasks.push(() => applicationObserver.disconnect());
      }
    }

    const handleFocusIn = (event) => {
      if (event.target.matches('input, select, textarea')) {
        fieldFocused = true;
        updateSticky();
      }
    };
    const handleFocusOut = () => {
      fieldFocused = false;
      updateSticky();
    };
    listen(document, 'focusin', handleFocusIn);
    listen(document, 'focusout', handleFocusOut);
    updateSticky();
  }

  const handleSectionLoad = () => init();
  const handleSectionUnload = () => {
    cleanup();
    requestAnimationFrame(init);
  };

  document.addEventListener('shopify:section:load', handleSectionLoad);
  document.addEventListener('shopify:section:unload', handleSectionUnload);
  window.addEventListener('pagehide', cleanup, { once: true });
  init();
})();
